import { yupResolver } from '@hookform/resolvers/yup';
import ArticleIcon from '@mui/icons-material/Article';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { Button, Stack, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { GenericFallbacksWrapper } from '../../../components/Fallback/GenericFallbacksWrapper';
import { FieldEntity, IField, isUniversalKPIField } from '../../../data-models/field2.data-model';
import {
  createKpiCollectionConfigurationModel,
  KpiCollectionConfiguration,
  kpiCollectionConfigurationSchema,
} from '../../../schemas/KpiCollectionConfiguration.schema';
import { kpiConfigAtom, universalAndSystemKPIAtom } from '../../../services/state/KPIConfigState';
import { colors } from '../../../theme/colors';
import {
  selectedKpiConfigAtom,
  showKpiConfigFormAtom,
  useKpiConfigFormDefaults,
} from '../kpi-config-form/kpi-config-form-state';
import { KpiConfigFormDialog } from '../kpi-config-form/KpiConfigFormDialog';
import { DCConfigFormHeader } from './DCConfigFormHeader';
import { KpiConfigList } from './KpiConfigList';

const ContentMaxWidth = '800px';

interface KpiCollectionConfigsProps {
  initialConfiguration?: KpiCollectionConfiguration;
}

export function KpiCollectionConfigs({ initialConfiguration }: KpiCollectionConfigsProps) {
  const kpiConfigs = useAtomValue(kpiConfigAtom);
  if (!kpiConfigs) {
    return null;
  }

  return (
    <GenericFallbacksWrapper>
      <DataCollectionConfigsContent initialConfiguration={initialConfiguration} />
    </GenericFallbacksWrapper>
  );
}

interface DataCollectionConfigsContentProps {
  initialConfiguration?: KpiCollectionConfiguration;
}

function DataCollectionConfigsContent({ initialConfiguration }: DataCollectionConfigsContentProps) {
  const universalKpis = useAtomValue(universalAndSystemKPIAtom);
  const customKpis = useAtomValue(kpiConfigAtom).filter((k) => k.entity === FieldEntity.userKPI);
  const setShowKpiConfigForm = useSetAtom(showKpiConfigFormAtom);
  const selectedKpiConfig = useAtomValue(selectedKpiConfigAtom);
  const formDefaults = useKpiConfigFormDefaults();

  const defaultKpis = useMemo(
    () => [
      ...universalKpis.filter(isUniversalKPIField).map((k) => k.id),
      ...customKpis.filter(isUniversalKPIField).map((k) => k.id),
    ],
    [universalKpis, customKpis]
  );

  const methods = useForm<KpiCollectionConfiguration>({
    defaultValues: initialConfiguration ?? createKpiCollectionConfigurationModel({ kpiIds: defaultKpis }),
    resolver: yupResolver(kpiCollectionConfigurationSchema()),
  });

  const handleKpiSubmit = useCallback(
    (data: IField<unknown>) => {
      const currentKpiIds = methods.getValues('kpiIds') ?? [];
      if (shouldAddKpiToConfig(data, currentKpiIds)) {
        methods.setValue('kpiIds', [...currentKpiIds, data.id], { shouldDirty: true });
      }
    },
    [methods]
  );

  return (
    <FormProvider {...methods}>
      <Stack p='1rem' direction='column' height='100%' maxHeight='100%' width='100%' gap='1rem'>
        <Stack maxWidth={ContentMaxWidth} gap='1rem'>
          <DCConfigFormHeader />
          <div>
            <Typography>Define reporting metrics</Typography>
            <Typography variant='body2' color='text.secondary'>
              Choose the metrics your portfolio companies will report. This configuration can be applied to
              multiple companies and reporting periods.
            </Typography>
          </div>
        </Stack>

        <Stack height='100%' overflow='auto'>
          <Stack maxWidth={ContentMaxWidth}>
            <KpiConfigList
              kpiConfigs={universalKpis}
              header={{
                title: 'KPIs',
                icon: <AssessmentIcon htmlColor={colors.secondary[40]} fontSize='large' />,
              }}
            />
            <Stack display='grid' gap='0.25rem' justifyItems='start'>
              <KpiConfigList
                kpiConfigs={customKpis}
                header={{
                  title: 'Other Questions',
                  icon: <ArticleIcon htmlColor={colors.secondary[40]} fontSize='large' />,
                }}
              />
              <Button size='medium' onClick={() => setShowKpiConfigForm(true)}>
                Create New
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <KpiConfigFormDialog
        title={selectedKpiConfig ? 'Edit KPI' : 'Create KPI'}
        onSubmit={handleKpiSubmit}
        defaultValues={formDefaults}
      />
    </FormProvider>
  );
}

export function shouldAddKpiToConfig(data: IField<unknown>, currentKpiIds: number[]): boolean {
  return data.entity === FieldEntity.userKPI && !currentKpiIds.includes(data.id);
}
